import { Play } from "lucide-react";
import InAppCall from "../InAppCall.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Separator } from "@/components/ui/separator.jsx";
import { Duration } from 'luxon';
import { Skeleton } from "@/components/ui/skeleton.jsx";


const ProspectCard = ({ secretShopperData, community, initiateCallback, logs, metrics, isLoading }) => {
    const isActive = secretShopperData?.is_active === true
    const hasInitiateLog = !secretShopperData?.has_initiated_log;

    const secretShopperMetrics = {
        time_to_email: metrics?.time_to_email || null,
        time_to_call: metrics?.time_to_call || null,
        time_to_text: metrics?.time_to_text || null,
        coverage: {
            email: metrics?.coverage?.email || false,
            call: metrics?.coverage?.call || false,
            text: metrics?.coverage?.text || false,
            voicemail: metrics?.coverage?.voicemail || false,
        },
    }

    const formatDuration = (seconds) => {
        if (seconds === null || seconds === undefined) return null;

        const dur = Duration.fromObject({ seconds }).shiftTo('days', 'hours', 'minutes', 'seconds').toObject();

        if (seconds < 60) {
            return `${Math.round(dur.seconds)}s`;
        }

        if (seconds < 3600) {
            return `${Math.floor(dur.minutes)}m`;
        }

        if (seconds < 86400) {
            return `${Math.floor(dur.hours)}h ${Math.floor(dur.minutes)}m`;
        }

        if (seconds < 604800) {
            return `${Math.floor(dur.days)}d ${Math.floor(dur.hours)}h`;
        }

        return `${Math.floor(dur.days)}d`;
    };

    if (isLoading) {
        return (
            <div className="w-full max-w-7xl mx-auto">
                <div className="grid grid-cols-2 gap-8">
                    <div className="bg-slate-50 rounded-lg p-4">
                        <div className="mb-3">
                            <Skeleton className="h-4 w-1/3" />
                        </div>
                        <div className="space-y-2 text-sm mb-4">
                            <Skeleton className="h-4 w-1/2" />
                            <Skeleton className="h-4 w-1/2" />
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-4 w-1/3" />
                        </div>
                        <Skeleton className="h-10 w-40" />
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                        <div className="mb-3">
                            <Skeleton className="h-4 w-1/3" />
                        </div>
                        <div className="space-y-2 text-sm mb-4">
                            <div className="flex justify-between">
                                <Skeleton className="h-4 w-1/3" />
                                <Skeleton className="h-4 w-1/4" />
                            </div>
                            <div className="flex justify-between">
                                <Skeleton className="h-4 w-1/3" />
                                <Skeleton className="h-4 w-1/4" />
                            </div>
                            <div className="flex justify-between">
                                <Skeleton className="h-4 w-1/3" />
                                <Skeleton className="h-4 w-1/4" />
                            </div>
                        </div>
                        <Skeleton className="h-px w-full my-3" />
                        <div className="mt-3 space-y-2">
                            <Skeleton className="h-4 w-1/3" />
                            <div className="grid grid-cols-2 gap-2">
                                <Skeleton className="h-5 w-24 rounded-full" />
                                <Skeleton className="h-5 w-24 rounded-full" />
                                <Skeleton className="h-5 w-24 rounded-full" />
                                <Skeleton className="h-5 w-24 rounded-full" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        )
    }

    return (
        <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-2 gap-8">
                <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-3">Prospect Details</h4>
                    <div className="space-y-2 text-sm mb-4">
                        <div className="text-slate-600">First Name: {secretShopperData.first_name}</div>
                        <div className="text-slate-600">Last Name: {secretShopperData.last_name}</div>
                        <div className="text-slate-600">Email: {secretShopperData.email}</div>
                        <div className="text-slate-600">Phone: {secretShopperData?.phone_number?.replace(/^(\+1)/, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</div>
                    </div>
                    {isActive && !!community?.community_phone_number && <Separator />}
                    <div className="mt-3">
                        {isActive && !hasInitiateLog && !!community?.community_phone_number && <InAppCall communityId={community.id} />}
                        {isActive && hasInitiateLog && !!community?.community_phone_number &&
                            <Button onClick={initiateCallback}>
                                <Play className="h-4 w-4 mr-2" /> Initiate Secret Shopper
                            </Button>
                        }
                    </div>
                </div>

                {logs !== undefined && <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-3">Score Breakdown</h4>
                    <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                            <span className="text-slate-600">Time To Email:</span>
                            <span className={secretShopperMetrics.time_to_email !== null ? "text-slate-900" : "text-slate-400"}>
                                {secretShopperMetrics.time_to_email !== null ? formatDuration(secretShopperMetrics.time_to_email) : "–"}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-600">Time To Call:</span>
                            <span className={secretShopperMetrics.time_to_call !== null ? "text-slate-900" : "text-slate-400"}>
                                {secretShopperMetrics.time_to_call !== null ? formatDuration(secretShopperMetrics.time_to_call) : "–"}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-600">Time To Text:</span>
                            <span className={secretShopperMetrics.time_to_text !== null ? "text-slate-900" : "text-slate-400"}>
                                {secretShopperMetrics.time_to_text !== null ? formatDuration(secretShopperMetrics.time_to_text) : "–"}
                            </span>
                        </div>
                    </div>
                    <Separator />
                    <div className="mt-3">
                        <div className="mb-2">
                            <span className="text-slate-600 text-sm">Channel Coverage:</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${secretShopperMetrics.coverage.email ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{secretShopperMetrics.coverage.email ? "✓" : "✕"}</div>
                                <span className="text-xs text-slate-600">Email</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${secretShopperMetrics.coverage.call ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{secretShopperMetrics.coverage.call ? "✓" : "✕"}</div>
                                <span className="text-xs text-slate-600">Call</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${secretShopperMetrics.coverage.text ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{secretShopperMetrics.coverage.text ? "✓" : "✕"}</div>
                                <span className="text-xs text-slate-600">Text</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${secretShopperMetrics.coverage.voicemail ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{secretShopperMetrics.coverage.voicemail ? "✓" : "✕"}</div>
                                <span className="text-xs text-slate-600">Voicemail</span>
                            </div>
                        </div>
                    </div>
                </div>}
            </div>
        </div>
    );
};

export default ProspectCard;